import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { ExpansionList, ExpansionPanel, SelectField, DatePicker, TextField } from "react-md";
import { cloneDeep } from "lodash";
import { Button } from "react-md/lib/Buttons";
import { licenseSchema } from "components/Appraiser/AppraiserProfileEdit";
import { getAllLicenses, addAppraiserLicense, updateAppraiserLicense, deleteAppraiserLicenseImage, deleteAppraiserLicense } from "requests/appraiser";
import { MDropzone, MCard } from "components/Shared";
import FilePreview from "containers/Homeowner/MCaseView/FileManager/FilePreview";
import WebConfig from "middlewares/WebConfig";
import { connect } from "react-redux";
import DialogContainer from "react-md/lib/Dialogs";
import TextBlock from "components/Shared/TextBlock";
import { APPRAISER_LICENSE_VIEW, APPRAISER_LICENSE_EDIT, APPRAISER_LICENSE_DELETE, APPRAISER_LICENSE_ADD } from "typeConstants/rights";

const statusList = [
  { name: "Valid", id: "VALID" },
  { name: "Expired", id: "EXPIRED" },
  { name: "Invalid", id: "INVALID" },
  { name: "Remove", id: "REMOVE" }];

const RenderLicenses = props => { // extends React.Component {
  const [licenses, setlicenses] = useState([]);
  const [expand, setexpand] = useState([]);
  const [dirty, setDirty] = useState(-1);
  const [previewFile, setPreviewFile] = useState(null);
  const [showDeleteLicenseDialog, setShowDeleteLicenseDialog] = useState(false);
  const [deletePaused, setDeletePaused] = useState(false);
  const [listenerAttached, setListenerAttached] = useState(false);

  let mounted = useRef(true);

  // The Appraiser list will preventDefault and cause issues when trying to upload a file so we need to stop propagation
  const mouseEventHandler = (x) => {
    x.stopPropagation();
  };

  const viewRight = props.rights.includes(APPRAISER_LICENSE_VIEW);
  const editRights = props.rights.includes(APPRAISER_LICENSE_EDIT);
  const addRights = props.rights.includes(APPRAISER_LICENSE_ADD);
  const deleteRights = props.rights.includes(APPRAISER_LICENSE_DELETE);

  const dropzoneRightsOverrides = {
    download: viewRight,
    preview: viewRight,
    delete: editRights,
  };

  useEffect(
    () => {
      if (editRights) {
        document.addEventListener("dragover", mouseEventHandler);
        setListenerAttached(true);
      }
      return () => {
        mounted.current = false;
        if (listenerAttached) document.removeEventListener("dragover", mouseEventHandler);
      };
    }, []);

  useEffect(() => {
    reload();
  }, [props.appraiserNumber]);

  const reload = () => {
    getAllLicenses(props.appraiserNumber, sortLicenses, mounted);
    setDirty(-1);
  };

  const sortLicenses = (licenses) => {
    licenses.sort((a, b) => b.expirationDate.localeCompare(a.expirationDate) ?? a.licenseState.name.localeCompare(b.licenseState.name));
    setlicenses(licenses);
  };

  const loadFiles = (data) => {
    if (!data || !data.licenseImageUrl) return [];
    const { webApiRoot } = WebConfig;
    return [{
      "canDelete": true,
      "downloadUrl": `${webApiRoot}/appraiserlicenses/${data.id}/image/download`,
      "fileName": "License file.jpg",
      "viewUrl": `${webApiRoot}/appraiserLicenses/${data.id}/image/view`
    }];
  };

  const blankLicense = () => {
    return {
      id: 0,
      appraiserNumber: props.appraiserNumber,
      licenseNumber: "",
      licenseCertificateType: { id: 0, name: "", code: "", description: "", appraisalSubcommitteeName: "" },
      licenseState: { id: 0, abbreviation: "", name: "", abbreviationAndName: "" },
      effectiveDate: "",
      expirationDate: "",
      licenseStatus: "VALID",
      hasLicenseImage: false,
      licenseImageUrl: "",
    };
  };

  const copyLicense = (data) => {
    let copiedLicense = blankLicense();
    copiedLicense.licenseCertificateType = cloneDeep(data.licenseCertificateType);
    copiedLicense.licenseNumber = data.licenseNumber;
    copiedLicense.licenseState = cloneDeep(data.licenseState);
    copiedLicense.licenseStatus = "VALID";
    return copiedLicense;
  };

  const primaryLabel = (license) => {
    if (license.id == 0) {
      // must be a new item
      return "New License";
    }
    return (license.licenseCertificateType.name);
  };
  const secondaryLabel = (license) => {
    if (license.id == 0) {
      // must be a new item
      return "";
    }
    return ((license.licenseStatus === "VALID" ? "" : license.licenseStatus + ":  ") + license.licenseNumber + " in " + license.licenseState.name);
  };

  const addAttachments = (files, data) => {
    if (!editRights) return;
    const filteredFiles = files.filter(({ file }) => file);
    if (filteredFiles.length !== 0)
      filteredFiles.map(({ file }) => {

        let reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = function (evt) {
          if (evt.target.readyState == FileReader.DONE) {
            let convertedfile = evt.target.result;
            let cleanfile = convertedfile.substring(convertedfile.lastIndexOf(",") + 1);
            data.licenseImage = cleanfile;
            saveLicense(data);
          }
          reader.onerror = function () {
            window.console.log("Error: Unable to load image");
          };
        };
      });

  };

  const saveLicense = (data) => {
    const successSave = () => {
      reload();
    };

    //standard request object when updating or adding an appraiser
    const getDTO = (data) => {
      return {
        "AppraiserNumber": props.appraiserNumber,
        "StateAbbreviation": data.licenseState.abbreviation,
        "Number": data.licenseNumber,
        "AppraiserLicenseCertificateTypeCode": data.licenseCertificateType.code,
        "EffectiveDate": data.effectiveDate,
        "ExpirationDate": data.expirationDate,
        "Status": data.licenseStatus,
        "LicenseImage": data.licenseImage
      };
    };

    const addAppraiser = (data) => {
      addAppraiserLicense(getDTO(data), successSave, mounted);
    };

    const updateAppraiser = (data) => {
      let requestObject = getDTO(data);
      requestObject["Id"] = data.id;
      updateAppraiserLicense(requestObject, successSave, mounted);
    };

    (data.id == 0) ? addAppraiser(data) : updateAppraiser(data);

  };

  const deleteAttachments = (data) => {
    const successDelete = () => {
      reload();
    };

    if (!editRights) return;
    deleteAppraiserLicenseImage(data.id, successDelete, mounted);
  };

  const deleteLicense = (data) => {
    if (!deleteRights) return;
    const successCallback = () => {
      setDeletePaused(false);
      reload();
    };
    const errorCallback = () => {
      setDeletePaused(false);
      reload();
    };
    setDeletePaused(true);
    deleteAppraiserLicense(data.id, successCallback, errorCallback, mounted);
  };

  const deleteActions = (currentItem, index) => deletePaused ? [<Button
    className="m-t-10"
    key="yes"
    raised
    primary
    disabled
  >
    deleting
  </Button>] : [
      <Button
        className="m-t-10"
        key="yes"
        raised
        primary
        onClick={() => {
          deleteLicense(currentItem);
          let newLicenses = cloneDeep(licenses);
          newLicenses.splice(index, 1);
          setlicenses(newLicenses);
          let newExpand = [];
          setexpand(newExpand);
          setShowDeleteLicenseDialog(false);
        }}
      >
        Yes
    </Button>,
      <Button
        className="m-t-10"
        key="nah"
        raised
        primary
        onClick={() => {
          setShowDeleteLicenseDialog(false);
        }}
      >
        No
    </Button>
    ];

  const openFilePreview = (file, urlWithSSO) => {
    let tab = window.open(urlWithSSO, "_blank");
    tab.focus();
  };

  let cardActions = previewFile ? [{ icon: "close", helpText: "Close Preview", onClick: () => setPreviewFile(null) }] : [];

  return (viewRight &&
    <div className="md-grid">
      <ExpansionList className="md-cell md-cell--12 md-cell--right">
        {licenses && licenses.map((license, index) => {
          let currentItem = license;
          return (<ExpansionPanel key={index}
            label={`${primaryLabel(currentItem)}`}
            secondaryLabel={`${secondaryLabel(currentItem)}`}
            saveProps={{
              flat: true,
              disabled: (!licenseSchema.isValidSync(license) || (dirty != index) || !editRights),
            }}
            expanded={expand[index] ? expand[index] : false}
            onExpandToggle={(flag) => {
              let newexpand = cloneDeep(expand);
              newexpand[index] = flag;
              setexpand(newexpand);
            }}
            cancelProps={{
              flat: true,
              disabled: (!editRights),
            }}

            onSave={() => saveLicense(currentItem)}
            onCancel={() => {
              if (!currentItem.id) {
                let newLicenses = cloneDeep(licenses);
                newLicenses.splice(index);

                setlicenses(newLicenses);
              }
              setDirty(-1);
              let newExpand = [];
              setexpand(newExpand);
            }}
            contentClassName="md-grid"
          >
            {(dirty == -1 && deleteRights) &&
              <Button
                primary
                raised
                children="View Order Queue"
                className="m-t-10 save"
                disabled={dirty != -1 || !editRights}
                onClick={() => {
                  setShowDeleteLicenseDialog(true);
                }}>Delete License
            </Button>
            }
            <DialogContainer
              id="dialog-complete-case"
              visible={showDeleteLicenseDialog}
              title="Delete License"
              modal
              width={500}
              focusOnMount={false}
              actions={deleteActions(currentItem, index)}
            >
              <TextBlock field="Are you sure?" />
            </DialogContainer>

            {(dirty == -1 && addRights) &&
              <Button
                primary
                raised
                children="View Order Queue"
                className="m-t-10 save"
                disabled={dirty != -1 || !editRights}
                onClick={() => {
                  let newLicenses = cloneDeep(licenses);
                  newLicenses.push(copyLicense(currentItem));
                  setlicenses(newLicenses);
                  let newExpand = [];
                  newExpand[newLicenses.length - 1] = true;
                  setexpand(newExpand);
                  setDirty(newLicenses.length - 1);
                }}>Copy License
            </Button>
            }
            {(dirty != -1 && dirty != index) && <div style={{ color: "red" }}> Unable to edit license with unsaved changes</div>}


            <TextField
              id="text-field-1"
              className="md-cell md-cell--12"
              label="License #"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              onChange={(value) => {
                let newLicenses = cloneDeep(licenses);
                newLicenses[index].licenseNumber = value;
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].licenseNumber ? licenses[index].licenseNumber : ""}
            />
            <SelectField
              id="select-field-1"
              className="md-cell md-cell--8"
              label="License Type"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              itemLabel="name"
              itemValue="id"
              menuItems={props.certificateList}
              onChange={(value) => {
                let newLicenses = cloneDeep(licenses);
                let selectedCertificate = props.certificateList.filter(x => { return x.id == value; });

                newLicenses[index].licenseCertificateType = selectedCertificate[0];
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].licenseCertificateType.id}
            />
            <SelectField
              id="select-field-2"
              className="md-cell md-cell--4 md-cell--right"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              label="State"
              itemLabel="stateName"
              itemValue="stateAbbreviation"
              menuItems={props.stateList}
              onChange={(value) => {
                let newLicenses = cloneDeep(licenses);
                let selectedState = props.stateList.filter(x => { return x.stateAbbreviation == value; });
                newLicenses[index].licenseState = { id: selectedState[0].id, abbreviation: selectedState[0].stateAbbreviation, name: selectedState[0].stateName };
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].licenseState.abbreviation}
            />
            <DatePicker
              id="date-field-1"
              className="md-cell md-cell--6"
              label="Effective Date"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              onChange={(date) => {
                let newLicenses = cloneDeep(licenses);
                newLicenses[index].effectiveDate = date;
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].effectiveDate ? new Date(licenses[index].effectiveDate).toLocaleDateString() : null}
            />
            <DatePicker
              id="date-field-2"
              className="md-cell md-cell--6 md-cell--right"
              label="Expiration Date"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              onChange={(date) => {
                let newLicenses = cloneDeep(licenses);
                newLicenses[index].expirationDate = date;
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].expirationDate ? new Date(licenses[index].expirationDate).toLocaleDateString() : null}
            />
            <SelectField
              id="select-field-3"
              className="md-cell md-cell--6 md-cell--right"
              required
              disabled={dirty != -1 && dirty != index || !editRights}
              label="Status"
              itemLabel="name"
              itemValue="id"
              menuItems={statusList}
              onChange={(value) => {
                let newLicenses = cloneDeep(licenses);
                newLicenses[index].licenseStatus = value;
                setDirty(index);
                setlicenses(newLicenses);
              }}
              value={licenses[index].licenseStatus}
            />
            {licenseSchema.isValidSync(license) &&
              <div className="manual-order-dropzone file-manager">
                <MCard
                  title="License File Manager"
                  className="full-height-card file-manager"
                  actions={cardActions}>

                  {previewFile ?
                    <FilePreview
                      displayname={previewFile.name}
                      path={previewFile.url}
                    /> :
                    <MDropzone
                      existingFiles={loadFiles(currentItem)}
                      multiple
                      noClick
                      onPreview={(file, urlWithSSO) => openFilePreview ? openFilePreview(file, urlWithSSO) : setPreviewFile({ name: file.name, url: urlWithSSO })}
                      onFileAdd={(file) => addAttachments(file, currentItem)}
                      onFileRemove={() => deleteAttachments(currentItem)}
                      prompt={editRights ? "Drop License File here" : "No License File attached"}
                      fileRightOverrides={dropzoneRightsOverrides}
                      columnDefinitionOverride={[
                        { name: "Name", dataSource: "name", className: "file-name-cell" },
                        { name: "Type", dataSource: "extension", style: { width: "50px" } },
                        { name: "Actions", dataSource: "actions", style: { width: "160px" } },
                      ]}
                    />
                  } </MCard>
              </div>
            }
          </ExpansionPanel>
          );
        })}
      </ExpansionList>
      {(dirty == -1 && addRights) && <Button className="md-cell md-cell--12 md-cell--right"
        flat
        secondary
        iconChildren="add"
        disabled={dirty != -1}
        onClick={() => {
          let newLicenses = cloneDeep(licenses);
          newLicenses.push(blankLicense());
          setlicenses(newLicenses);
          let newExpand = [];
          newExpand[newLicenses.length - 1] = true;
          setexpand(newExpand);
          setDirty(newLicenses.length - 1);
        }}>Add License
          </Button>}
    </div>
  );
};

RenderLicenses.propTypes = {
  appraiserNumber: PropTypes.number,
  certificateList: PropTypes.array,
  stateList: PropTypes.array,
  roles: PropTypes.array,
  rights: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    roles: state.user.profile.roles,
    rights: state.user.profile.rights,
    certificateList: state.appraiser.certificateList,
    stateList: state.appraiser.stateList,
  };
};

export default connect(mapStateToProps)(RenderLicenses);
